import * as React from 'react';
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { StoreContext } from '../../store/StoreProvider';
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete from "@mui/material/Autocomplete";
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BarChartIcon from '@mui/icons-material/BarChart';
import VerifiedIcon from '@mui/icons-material/Verified';
import moment from 'moment';
import MobileDatePicker from '@mui/lab/MobileDatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterMoment from '@mui/lab/AdapterMoment';
import { getI18N } from '../../i18n';
import { DashGardiAutocomplete, DashGardiReport } from '../../store/sagas/Warranty';

interface DashboardProps { }

interface FilterState {
  fechaDesde: Date | null;
  fechaHasta: Date | null;
  marca: string;
  modelo: string;
}

interface ReportVigencia {
  vigentes: number;
  porVencer: number;
  vencidos: number;
}

interface ReportItem {
  nombre: string;
  porcentaje: number;
}

interface ReportData {
  vigencia: ReportVigencia;
  modelos: ReportItem[];
  tipoCombustion: ReportItem[];
  tipoUso: ReportItem[];
  garantia: ReportItem[];
}

interface DonutData {
  label: string;
  value: number;
  color: string;
}

interface BarData {
  label: string;
  value: number;
  pct: number;
}

export const DashboardComponent = (props: DashboardProps) => {
  const [store] = React.useContext(StoreContext);
  const { Country } = store?.user;
  const currentLocale: string = Country || "PE";
  const i18n = getI18N({ currentLocale });

  const [filters, setFilters] = React.useState<FilterState>({
    fechaDesde: moment().subtract(30, 'days').toDate(),
    fechaHasta: moment().toDate(),
    marca: '',
    modelo: ''
  });

  const [marcas, setMarcas] = React.useState<{ id: number; nombre: string }[]>([]);
  const [modelos, setModelos] = React.useState<{ id: number; nombre: string }[]>([]);
  const [reportData, setReportData] = React.useState<ReportData | null>(null);
  const [loadingMarcas, setLoadingMarcas] = React.useState(false);
  const [loadingModelos, setLoadingModelos] = React.useState(false);
  const [loadingReport, setLoadingReport] = React.useState(false);
  const [openFechaDesde, setOpenFechaDesde] = React.useState(false);
  const [openFechaHasta, setOpenFechaHasta] = React.useState(false);
  const acceptFechaDesde = React.useRef(false);
  const acceptFechaHasta = React.useRef(false);

  const timeoutRef = React.useRef<{ marca: ReturnType<typeof setTimeout> | null; modelo: ReturnType<typeof setTimeout> | null }>({ marca: null, modelo: null });

  const buscarMarcas = async (busqueda: string) => {
    setLoadingMarcas(true);
    try {
      const json = await DashGardiAutocomplete({ tipo: 'marca', busqueda, limite: 20 });
      if (json.status === 'ok') {
        setMarcas(json.data || []);
      }
    } catch (e) {
      console.error('Error buscando marcas', e);
    } finally {
      setLoadingMarcas(false);
    }
  };

  const buscarModelos = async (busqueda: string) => {
    setLoadingModelos(true);
    try {
      const json = await DashGardiAutocomplete({ tipo: 'modelo', busqueda, limite: 20 });
      if (json.status === 'ok') {
        setModelos(json.data || []);
      }
    } catch (e) {
      console.error('Error buscando modelos', e);
    } finally {
      setLoadingModelos(false);
    }
  };

  const fetchReport = async () => {
    setLoadingReport(true);

    const clientId: string = store?.selected_client?.UserId;

    try {
      const payload: any = {
        fecha_inicio: moment(filters.fechaDesde).format('YYYY-MM-DD'),
        fecha_fin: moment(filters.fechaHasta).format('YYYY-MM-DD'),
        cardCode: clientId
      };
      if (filters.marca) payload.marca = filters.marca;
      if (filters.modelo) payload.modelo = filters.modelo;

      const json = await DashGardiReport(payload);
      if (json.status === 99 && json.data) {
        setReportData(json.data);
      }
    } catch (e) {
      console.error('Error cargando reporte', e);
    } finally {
      setLoadingReport(false);
    }
  };

  const debouncedBuscarMarcas = (value: string) => {
    if (timeoutRef.current.marca) clearTimeout(timeoutRef.current.marca);
    timeoutRef.current.marca = setTimeout(() => buscarMarcas(value), 400);
  };

  const debouncedBuscarModelos = (value: string) => {
    if (timeoutRef.current.modelo) clearTimeout(timeoutRef.current.modelo);
    timeoutRef.current.modelo = setTimeout(() => buscarModelos(value), 400);
  };

  React.useEffect(() => {
    buscarMarcas('');
    buscarModelos('');
  }, []);

  React.useEffect(() => {
    fetchReport();
  }, [filters.fechaDesde, filters.fechaHasta, filters.marca, filters.modelo]);

  const handleClearFilters = () => {
    setFilters({
      fechaDesde: moment().subtract(30, 'days').toDate(),
      fechaHasta: moment().toDate(),
      marca: '',
      modelo: ''
    });
  };

  const totalActivaciones = reportData
    ? reportData.vigencia.vigentes + reportData.vigencia.porVencer + reportData.vigencia.vencidos
    : 0;

  const COLORS_COMBUSTION = ['#E6A817', '#1C1C1C', '#BDBDBD', '#9E9E9E', '#E0E0E0', '#757575'];
  const COLORS_USO = ['#E6A817', '#1C1C1C', '#BDBDBD', '#9E9E9E', '#E0E0E0', '#757575'];
  const COLORS_GARANTIA = ['#E6A817', '#1C1C1C', '#BDBDBD', '#9E9E9E', '#E0E0E0', '#757575'];

  const activacionesPorModelo: BarData[] = reportData
    ? reportData.modelos.map((m: ReportItem) => ({ label: m.nombre, value: 0, pct: m.porcentaje }))
    : [];

  const tipoCombustion: DonutData[] = reportData
    ? reportData.tipoCombustion.map((c: ReportItem, i: number) => ({ label: c.nombre, value: c.porcentaje, color: COLORS_COMBUSTION[i % COLORS_COMBUSTION.length] }))
    : [];

  const tipoUso: DonutData[] = reportData
    ? reportData.tipoUso.map((u: ReportItem, i: number) => ({ label: u.nombre, value: u.porcentaje, color: COLORS_USO[i % COLORS_USO.length] }))
    : [];

  const plazoGarantia: DonutData[] = reportData
    ? reportData.garantia.map((g: ReportItem, i: number) => ({ label: g.nombre, value: g.porcentaje, color: COLORS_GARANTIA[i % COLORS_GARANTIA.length] }))
    : [];

  const buildConicGradient = (data: DonutData[]) => {
    const total = data.reduce((s, d) => s + d.value, 0);
    let acc = 0;
    const stops: string[] = [];
    data.forEach((d) => {
      const start = (acc / total) * 360;
      acc += d.value;
      const end = (acc / total) * 360;
      stops.push(`${d.color} ${start}deg ${end}deg`);
    });
    return `conic-gradient(${stops.join(', ')})`;
  };

  const DonutChart = ({ title, data, icon }: { title: string; data: DonutData[]; icon?: React.ReactNode }) => {
    return (
      <div className="dashboard-chart-card-inner">
        <div className="dashboard-chart-title">{title}</div>
        <div className="dashboard-donut-body">
          <div className="dashboard-donut-ring" style={{ background: buildConicGradient(data) }}>
            <div className="dashboard-donut-hole">
              {icon && <div className="dashboard-donut-icon">{icon}</div>}
            </div>
          </div>
          <div className="dashboard-donut-legend">
            {data.map((d, i) => (
              <div key={i} className="dashboard-legend-row">
                <span className="dashboard-legend-dot" style={{ backgroundColor: d.color }} />
                <span className="dashboard-legend-label">{d.label}</span>
                <span className="dashboard-legend-value">{d.value}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="dashboard-chart-footer-link">VER DETALLE</div>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      <Box sx={{ flexGrow: 1, pl: 1, pr: 1 }}>
        <div className="dashboard-content">

          <Paper className="dashboard-filters" elevation={0}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Typography variant="caption" className="dashboard-filter-label">
                  FILTRO POR FECHA
                </Typography>
                <div className="dashboard-date-range">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <MobileDatePicker
                      label="Desde"
                      value={filters.fechaDesde}
                      open={openFechaDesde}
                      onOpen={() => setOpenFechaDesde(true)}
                      onChange={() => { }}
                      onAccept={(newValue: any) => {
                        acceptFechaDesde.current = true;
                        setFilters({ ...filters, fechaDesde: newValue });
                        setOpenFechaDesde(false);
                      }}
                      onClose={() => {
                        if (!acceptFechaDesde.current) {
                          setOpenFechaDesde(false);
                        }
                        acceptFechaDesde.current = false;
                      }}
                      DialogProps={{ onBackdropClick: () => { } }}
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          size="small"
                          className="dashboard-date-input"
                          onClick={() => setOpenFechaDesde(true)}
                        />
                      )}
                    />
                    <MobileDatePicker
                      label="Hasta"
                      value={filters.fechaHasta}
                      open={openFechaHasta}
                      onOpen={() => setOpenFechaHasta(true)}
                      onChange={() => { }}
                      onAccept={(newValue: any) => {
                        acceptFechaHasta.current = true;
                        setFilters({ ...filters, fechaHasta: newValue });
                        setOpenFechaHasta(false);
                      }}
                      onClose={() => {
                        if (!acceptFechaHasta.current) {
                          setOpenFechaHasta(false);
                        }
                        acceptFechaHasta.current = false;
                      }}
                      DialogProps={{ onBackdropClick: () => { } }}
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          size="small"
                          className="dashboard-date-input"
                          onClick={() => setOpenFechaHasta(true)}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </div>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="caption" className="dashboard-filter-label">MARCA:</Typography>
                <Autocomplete
                  size="small"
                  options={marcas}
                  loading={loadingMarcas}
                  value={filters.marca ? marcas.find((m) => m.nombre === filters.marca) || null : null}
                  getOptionLabel={(option) => option.nombre}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onInputChange={(_, value) => debouncedBuscarMarcas(value)}
                  onChange={(_, value) => setFilters({ ...filters, marca: value?.nombre || '' })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Buscar marca"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="caption" className="dashboard-filter-label">MODELO:</Typography>
                <Autocomplete
                  size="small"
                  options={modelos}
                  loading={loadingModelos}
                  value={filters.modelo ? modelos.find((m) => m.nombre === filters.modelo) || null : null}
                  getOptionLabel={(option) => option.nombre}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onInputChange={(_, value) => debouncedBuscarModelos(value)}
                  onChange={(_, value) => setFilters({ ...filters, modelo: value?.nombre || '' })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Buscar modelo"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={handleClearFilters}
                  className="dashboard-clear-btn"
                >
                  Limpiar filtros
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <div className="dashboard-kpi-card">
            {/*<svg className="dashboard-kpi-sparkline" viewBox="0 0 120 50" preserveAspectRatio="none">
              <defs>
                <linearGradient id="kpiFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(0,0,0,0.10)" />
                  <stop offset="100%" stopColor="rgba(0,0,0,0)" />
                </linearGradient>
              </defs>
              <path
                d="M0,36 L4,36 L4,32 L10,32 L10,34 L14,34 L14,28 L18,28 L18,24 L22,24 L22,26 L26,26 L26,22 L32,22 L32,18 L36,18 L36,20 L40,20 L40,16 L44,16 L44,14 L50,14 L50,16 L54,16 L54,12 L58,12 L58,10 L64,10 L64,8 L68,8 L68,10 L72,10 L72,6 L76,6 L76,4 L82,4 L82,6 L86,6 L86,4 L90,4 L90,3 L96,3 L96,2 L100,2 L100,1 L106,1 L106,2 L110,2 L110,1 L116,1 L116,0 L120,0 L120,50 L0,50 Z"
                fill="url(#kpiFill)"
              />
              
              <path
                d="M0,36 L4,36 L4,32 L10,32 L10,34 L14,34 L14,28 L18,28 L18,24 L22,24 L22,26 L26,26 L26,22 L32,22 L32,18 L36,18 L36,20 L40,20 L40,16 L44,16 L44,14 L50,14 L50,16 L54,16 L54,12 L58,12 L58,10 L64,10 L64,8 L68,8 L68,10 L72,10 L72,6 L76,6 L76,4 L82,4 L82,6 L86,6 L86,4 L90,4 L90,3 L96,3 L96,2 L100,2 L100,1 L106,1 L106,2 L110,2 L110,1 L116,1 L116,0 L120,0"
                fill="none"
                stroke="rgba(0,0,0,0.22)"
                strokeWidth="0.7"
                strokeLinecap="butt"
                strokeLinejoin="miter"
              />
            </svg>*/}
            <div className="dashboard-kpi-content">
              <div className="dashboard-kpi-left">
                <span className="dashboard-kpi-label">FILTRO POR FECHA:</span>
                <span className="dashboard-kpi-value">{loadingReport ? '...' : totalActivaciones.toLocaleString()}</span>
                <span className="dashboard-kpi-subtitle">Activaciones totales en el periodo seleccionado</span>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6} lg={3}>
              <Paper className="dashboard-chart-card" elevation={0}>
                <div className="dashboard-chart-card-inner">
                  <div className="dashboard-chart-title">ACTIVACIONES POR MODELO</div>
                  <div className="dashboard-bar-body">
                    {activacionesPorModelo.map((item, idx) => (
                      <div key={idx} className="dashboard-bar-row">
                        <span className="dashboard-bar-label">{item.label}</span>
                        <div className="dashboard-bar-track">
                          <div className="dashboard-bar-fill" style={{ width: `${item.pct}%` }} />
                        </div>
                        <span className="dashboard-bar-pct">{item.pct}%</span>
                      </div>
                    ))}
                  </div>
                  <div className="dashboard-chart-footer-link">VER DETALLE</div>
                </div>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Paper className="dashboard-chart-card" elevation={0}>
                <DonutChart title="TIPO DE COMBUSTIÓN" data={tipoCombustion} icon={<LocalGasStationIcon sx={{ fontSize: 36, color: '#555' }} />} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Paper className="dashboard-chart-card" elevation={0}>
                <DonutChart title="TIPO DE USO" data={tipoUso} icon={<DirectionsCarIcon sx={{ fontSize: 36, color: '#555' }} />} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Paper className="dashboard-chart-card" elevation={0}>
                <DonutChart title="PLAZO DE GARANTÍA" data={plazoGarantia} />
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper className="dashboard-status-card" elevation={0} sx={{ backgroundColor: '#d4edda' }}>
                <div className="dashboard-status-icon-wrapper" style={{ backgroundColor: '#28a745' }}>
                  <CheckCircleIcon sx={{ fontSize: 30, color: '#fff' }} />
                </div>
                <div className="dashboard-status-info">
                  <Typography variant="h4" className="dashboard-status-value">{loadingReport ? '...' : (reportData?.vigencia.vigentes || 0).toLocaleString()}</Typography>
                  <Typography variant="body2" className="dashboard-status-label">Vigente</Typography>
                  <Typography variant="caption" className="dashboard-status-percentage">{totalActivaciones > 0 ? Math.round(((reportData?.vigencia.vigentes || 0) / totalActivaciones) * 100) : 0}% del total</Typography>
                </div>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className="dashboard-status-card" elevation={0} sx={{ backgroundColor: '#ffe5cc' }}>
                <div className="dashboard-status-icon-wrapper" style={{ backgroundColor: '#e67e22' }}>
                  <WarningAmberIcon sx={{ fontSize: 30, color: '#fff' }} />
                </div>
                <div className="dashboard-status-info">
                  <Typography variant="h4" className="dashboard-status-value">{loadingReport ? '...' : (reportData?.vigencia.porVencer || 0).toLocaleString()}</Typography>
                  <Typography variant="body2" className="dashboard-status-label">Por vencer</Typography>
                  <Typography variant="caption" className="dashboard-status-percentage">{totalActivaciones > 0 ? Math.round(((reportData?.vigencia.porVencer || 0) / totalActivaciones) * 100) : 0}% del total</Typography>
                </div>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper className="dashboard-status-card" elevation={0} sx={{ backgroundColor: '#f8d7da' }}>
                <div className="dashboard-status-icon-wrapper" style={{ backgroundColor: '#dc3545' }}>
                  <WarningAmberIcon sx={{ fontSize: 30, color: '#fff' }} />
                </div>
                <div className="dashboard-status-info">
                  <Typography variant="h4" className="dashboard-status-value">{loadingReport ? '...' : (reportData?.vigencia.vencidos || 0).toLocaleString()}</Typography>
                  <Typography variant="body2" className="dashboard-status-label">Vencido</Typography>
                  <Typography variant="caption" className="dashboard-status-percentage">{totalActivaciones > 0 ? Math.round(((reportData?.vigencia.vencidos || 0) / totalActivaciones) * 100) : 0}% del total</Typography>
                </div>
              </Paper>
            </Grid>
          </Grid>

        </div>
      </Box>
    </div>
  );
};
